/* The doc surface's background — sampled colors are pre-blended onto this so
   a translucent callout wash shows in the minimap as it looks on the page,
   regardless of what sits behind the rail.
   Matches --doc-sheet-surface (#121519). */
const PAGE_BG = [18, 21, 25];

/**
 * Parse legacy `rgba(r, g, b, a)` and modern `rgb(r g b / a)` (also %).
 * Returns [r, g, b, a] with a in 0..1, or null.
 */
const parseCssColor = (colorStr) => {
  if (!colorStr) return null;
  const m = String(colorStr).match(/rgba?\(([^)]+)\)/i);
  if (!m) return null;
  const body = m[1].trim();

  let r;
  let g;
  let b;
  let a = 1;

  if (body.includes(',')) {
    const parts = body.split(',').map((v) => v.trim());
    r = parseFloat(parts[0]);
    g = parseFloat(parts[1]);
    b = parseFloat(parts[2]);
    if (parts[3] != null) {
      const raw = parts[3].trim();
      a = raw.endsWith('%') ? parseFloat(raw) / 100 : parseFloat(raw);
    }
  } else {
    // `r g b` or `r g b / a`
    const [rgbPart, alphaPart] = body.split('/').map((s) => s.trim());
    const channels = rgbPart.split(/\s+/).map((v) => parseFloat(v));
    if (channels.length < 3) return null;
    [r, g, b] = channels;
    if (alphaPart != null) {
      a = alphaPart.endsWith('%') ? parseFloat(alphaPart) / 100 : parseFloat(alphaPart);
    }
  }

  if ([r, g, b, a].some((v) => Number.isNaN(v))) return null;
  return [r, g, b, Math.min(1, Math.max(0, a))];
};

/** rgba/rgb(...) → solid rgb() composited over an opaque [r,g,b] base. */
const blendOnto = (rgbaStr, baseRgb) => {
  const parsed = parseCssColor(rgbaStr);
  if (!parsed || !baseRgb) return null;
  const [r, g, b, a] = parsed;
  const mix = (c, base) => Math.round(c * a + base * (1 - a));
  return `rgb(${mix(r, baseRgb[0])}, ${mix(g, baseRgb[1])}, ${mix(b, baseRgb[2])})`;
};

/** rgba/rgb(...) string → solid rgb() as it would render over the page bg. */
export const blendOntoPage = (rgbaStr) => blendOnto(rgbaStr, PAGE_BG);

/**
 * A callout's ACTUAL surface: fill mode → background tint; border mode → the
 * boxShadow line color, flagged so the mini-box renders outlined.
 */
export const calloutSurface = (calloutEl) => {
  const cs = getComputedStyle(calloutEl);
  let source = cs.backgroundColor;
  let borderMode = false;
  if (!source || source === 'rgba(0, 0, 0, 0)' || source === 'transparent') {
    source = cs.boxShadow || '';
    borderMode = true;
  }
  const m = String(source).match(/rgba?\([^)]+\)/i);
  if (!m) return null;
  const color = blendOntoPage(m[0]);
  return color ? { color, borderMode } : null;
};

/**
 * Code block panel + line ink. Text is composited onto the PANEL (not the
 * page), matching the editor. 1px hairlines read hotter than body text, so
 * the ink is eased ~35% back toward the panel after compositing.
 */
export const codeSurface = (codeBlockEl) => {
  if (!codeBlockEl) return null;
  const panel = getComputedStyle(codeBlockEl);
  const fill =
    blendOntoPage(panel.backgroundColor) || panel.backgroundColor || 'rgb(26, 31, 39)';
  const fillRgb = parseCssColor(fill) || [26, 31, 39, 1];
  const textarea = codeBlockEl.querySelector(
    '.code-block-editable, .code-block-textarea'
  );
  const lineCs = textarea ? getComputedStyle(textarea) : panel;
  const onPanel =
    blendOnto(lineCs.color, fillRgb) || blendOnto('rgba(255, 255, 255, 0.9)', fillRgb);
  const ink = parseCssColor(onPanel);
  if (!ink) return { fill, line: 'rgba(255, 255, 255, 0.4)' };
  // Soften for hairline weight (keeps hue of real text, less neon in the rail)
  const soften = 0.62;
  const line = `rgb(${[0, 1, 2]
    .map((i) => Math.round(ink[i] * soften + fillRgb[i] * (1 - soften)))
    .join(', ')})`;
  return { fill, line };
};
